import { Box, Grid, Typography, useMediaQuery } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import LoadingSpinner from '@/components/LoadingSpinner'
import {
  usePlanCheckoutMutation,
  usePlanUpgradeMutation,
} from '@/store/apiService'
import { setActivePlan } from '@/store/appSlice'
import { getPlansApi, getProfileApi } from '@/utils/appApi'
import { PLAN_TEXTGRADIENT } from '@/utils/constants'

import ComparePlans from './ComparePlan'
import MobileCompareplans from './MobileComparePlan'
import PlanCard from './PlanCard'
import PlanToggle from './PlanToggle'

type PlanInterval = 'month' | 'year'

const PlanBadgeWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      p: '0.625rem 0.5625rem 0.5625rem 0.5625rem',
      borderRadius: '1rem 1rem 0.5rem 0.5rem',
      bgcolor: '#90CAF9',
      textAlign: 'center',
      width: 'fit-content',
    }}
  >
    <Typography
      sx={{
        color: '#000',
        textTransform: 'uppercase',
        fontSize: '14px',
        fontWeight: 700,
      }}
    >
      Best Value
    </Typography>
    {children}
  </Box>
)

const Plans = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'plans' })
  const [selectedPlan, setSelectedPlan] = useState<PlanInterval>('year')
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [upgradePlanId, setUpgradePlanId] = useState()
  const [planData, setPlans] = useState<any[]>([])
  const [isPlansLoading, setIsPlansLoading] = useState(true)

  const [profileData, setProfileData] = useState<any>(null)
  const activePlan = profileData?.subscriptions
  const activeplanId = profileData?.subscriptions?.[0]?.plan?.id
  const activePlanInterval = profileData?.subscriptions?.[0]?.plan?.interval
  const tablet = useMediaQuery('(max-width:768px)')

  const dispatch = useDispatch()

  const manualRefetchProfile = async () => {
    try {
      const data = await getProfileApi()
      setProfileData(data)
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    }
  }

  useEffect(() => {
    manualRefetchProfile()
  }, [])

  useEffect(() => {
    getPlansApi()
      .then((res) => {
        setPlans(res)
        setIsPlansLoading(false)
      })
      .catch(() => {
        setIsPlansLoading(false)
      })
  }, [])

  useEffect(() => {
    if (profileData?.subscriptions?.length > 0) {
      dispatch(setActivePlan(profileData.subscriptions[0]))
    }
  }, [profileData, dispatch])

  const [planCheckOut, { isLoading: isCheckingOut }] = usePlanCheckoutMutation()
  const [planUpgrade, { isLoading: isUpgrading }] = usePlanUpgradeMutation()

  const newAvailablePlans = useMemo(() => {
    const monthlyPlans: any[] = []
    const yearlyPlans: any[] = []

    planData?.forEach((planGroup: any, index: number) => {
      const baseDetails = {
        name: planGroup.name,
        summary: planGroup.summary,
        description: planGroup.description,
        badge: planGroup.badge,
        textGradient: PLAN_TEXTGRADIENT[index],
      }

      planGroup.plans.forEach((plan: any) => {
        const fullPlan = { ...plan, ...baseDetails }
        if (plan.interval === 'month') {
          monthlyPlans.push(fullPlan)
        } else if (plan.interval === 'year') {
          yearlyPlans.push(fullPlan)
        }
      })
    })

    return {
      month: monthlyPlans,
      year: yearlyPlans,
    }
  }, [planData])

  const activePlanName = useMemo(() => {
    if (!planData || !activeplanId) return ''
    for (const planGroup of planData) {
      if (planGroup.plans.some((p: any) => p.id === activeplanId)) {
        return planGroup.name
      }
    }
    return ''
  }, [planData, activeplanId])

  const choosePlan = async (id, user_confirm = false, trial = false) => {
    if (activePlan?.length > 0) {
      setConfirmationModalOpen(true)
      setUpgradePlanId(id)
    } else {
      try {
        const response = await planCheckOut({
          plan: id,
          trial,
          user_confirm,
          redirect_url: `${window.location.origin}/stripe-waiting`,
        }).unwrap()

        await manualRefetchProfile()
        if (response?.url) {
          window.location.href = response?.url
        }
      } catch (error) {
        const code = error?.data?.code

        switch (code) {
          case 3001:
            toast.error('Plan does not exist.')
            break
          case 3002:
            toast.error('Subscription does not exist')
            break
          case 3003:
            toast.error('Plan already exists.')
            break
          case 3004:
            toast.error('Checkout session is active')
            break
          case 3005:
          case 3006:
            toast.error('Multiple Sessions detected')
            break
          case 3007:
            toast.error('Already active plan on Stripe.')
            setTimeout(() => {
              window.location.href = error?.data?.url
            }, 4000)
            break
          case 3008:
            toast.error('Not allowed to perform this action')
            break
          case 3010:
            Swal.fire({
              text: 'Please note that you already have an mobile plan active, so you will have two plans active that support the same features. You may manage your mobile subscription in the iOS app store.',
              icon: 'warning',
              showCancelButton: true,
              showConfirmButton: true,
              cancelButtonColor: '#d33',
              allowOutsideClick: false,
              confirmButtonColor: '#3085d6',
              confirmButtonText: 'Confirm',
              cancelButtonText: 'Cancel',
            }).then((result) => {
              if (!result.isConfirmed) {
                return
              } else {
                choosePlan(id, true)
              }
            })
        }
      }
    }
  }

  const handleConfirmPlan = async () => {
    setConfirmationModalOpen(false)
    toast.info('Processing your plan change.')

    try {
      const response = await planUpgrade({
        stripe_subscription:
          activePlan?.[activePlan?.length - 1]?.stripe_subscription,
        plan: upgradePlanId,
      }).unwrap()

      await manualRefetchProfile()
      toast.success('Plan changed successfully!')

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (error) {
      const code = error?.data?.code

      if (code === 3004) {
        toast.error('Checkout session is active')
      }

      if (code === 3011) {
        toast.error(error.data.message)
      }
    }
  }

  const renderPlan = (plan: any, index: number) => {
    const planCard = (
      <PlanCard
        index={index}
        title={plan.name}
        textBackground={plan.textGradient}
        mainDescription={plan.summary}
        price={plan.amount}
        duration={`per ${plan.interval}`}
        recipeDescription={plan.description}
        bestValue={!!plan.badge}
        buttonText={plan?.id === activeplanId ? 'YOUR CHOICE' : 'SELECT PLAN'}
        planName={plan.name}
        selectedPlan={selectedPlan}
        isDisabled={plan?.id === activeplanId}
        onStartTrial={async () => choosePlan(plan.id)}
      />
    )

    return (
      <Grid item md={3} key={index}>
        {plan.badge ? (
          <PlanBadgeWrapper>{planCard}</PlanBadgeWrapper>
        ) : (
          <Box sx={{ pt: '2.2rem' }}>{planCard}</Box>
        )}
      </Grid>
    )
  }

  return (
    <Box overflow="auto" flex={1}>
      <Box mt={5} mx="auto">
        <Box display="flex" justifyContent="center" mb={3}>
          <Typography fontSize="36px" fontWeight={600}>
            {t('choosePlan')}
          </Typography>
        </Box>

        <PlanToggle selectedPlan={selectedPlan} onChange={setSelectedPlan} />

        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          gap="2.5rem"
          mt="1rem"
        >
          {isPlansLoading ? (
            <Box py={20}>
              <LoadingSpinner />
            </Box>
          ) : (
            newAvailablePlans[selectedPlan]?.map(renderPlan)
          )}
        </Box>
        {tablet ? (
          <MobileCompareplans />
        ) : (
          <ComparePlans
            selectedPlan={selectedPlan}
            activePlanName={activePlanName}
            activePlanInterval={activePlanInterval}
            data={newAvailablePlans[selectedPlan]}
            onStartFreeTrial={async (planId) => choosePlan(planId)}
          />
        )}
      </Box>

      <Dialog
        triggerElement={null}
        isOpen={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        title={t('planModifyDialog.title')}
        content={null}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <>
            <Button
              variant="contained"
              label={t('planModifyDialog.cancelButton')}
              fullWidth
              onClick={() => setConfirmationModalOpen(false)}
            />
            <Button
              variant="contained"
              label={t('planModifyDialog.confirmButton')}
              fullWidth
              onClick={handleConfirmPlan}
              loading={isUpgrading}
              disabled={isUpgrading || isCheckingOut}
            />
          </>
        }
      />
    </Box>
  )
}

export default Plans
